// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo } from "@insite/mobius/Icon";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FloatingTooltip, { FloatingTooltipStyles } from "@synd/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyles } from "@synd/App/Components/Header/HeaderClickable";
import MiniCart from "@synd/App/Components/MiniCart/MiniCart";
import { CustomAppState } from "@synd/App/Store/Reducers";
import HeaderCart from "@synd/XDL/Icons/HeaderCart";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    /**
     * XNG-Changes
     */
    isCartPage: getCurrentPage(state).type === "CartPage",
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // XNG-Changes
    headerClickable?: HeaderClickableStyles;
    xngTooltip?: FloatingTooltipStyles;
    countText?: TypographyPresentationProps;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: ShoppingCart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    // XNG-Changes
    isCartPage,
    isTabletScreen,
}) => {
    // XNG-Changes
    const [isMenuOpen, setMenuOpen] = useState(false);
    useEffect(() => {
        // XNG-Changes: If we are on the cart page, we don't need to have any race-condition.
        if (shouldLoadCart && !isCartPage) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart, isCartPage]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    // XNG-Changes: add isTabletScreen
    const showLabel = visibilityState !== "icon" && !isTabletScreen;

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;
    const closePopup = () => {
        setMenuOpen(false);
    };

    // if (isCompactHeaderCart) {
    //     return (
    //         <Link
    //             {...routerLink}
    //             color={theme.header.linkColor}
    //             href={cartUrl}
    //             icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
    //             data-test-selector="cartLink"
    //         >
    //             <Typography as="span" {...styles.compactCountText}>
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //             <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
    //         </Link>
    //     );
    // }

    // return (
    //     <Link
    //         {...routerLink}
    //         href={cartUrl}
    //         icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
    //         data-test-selector="cartLink"
    //     >
    //         <Hidden below="lg" as="span">
    //             {showLabel && `${translate("Cart")} `}(
    //             <Typography as="span" {...styles.desktopCountText} data-test-selector="cartLinkQuantity">
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //             )
    //         </Hidden>
    //         <Hidden above="md">
    //             <Typography as="span" {...styles.mobileCountText}>
    //                 {retainedTotalCountToDisplay}
    //             </Typography>
    //         </Hidden>
    //         <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
    //     </Link>
    // );
    return (
        <FloatingTooltip
            onOpenChange={open => setMenuOpen(open)}
            offset={{
                mainAxis: 17,
            }}
            delay={{
                open: 700,
                close: 400,
            }}
            trigger="click"
            placement="bottom-end"
            isOpen={isMenuOpen}
            triggerComponent={
                <HeaderClickable
                    isOpen={isMenuOpen}
                    href={isTabletScreen ? cartUrl : undefined}
                    data-test-selector="cartLink"
                    aria-label={translate("Mini-Cart")}
                    extendedStyles={styles.headerClickable}
                    showIcon={showIcon}
                    icon={{
                        src: HeaderCart,
                    }}
                    customReplacement={
                        totalCountDisplay && isTabletScreen ? (
                            <Typography {...styles.countText}>{totalCountDisplay}</Typography>
                        ) : null
                    }
                    referenceId="mini-cart-reference"
                >
                    {showLabel &&
                        (totalCountDisplay ? translate("Cart ({0})", totalCountDisplay.toString()) : translate("Cart"))}
                </HeaderClickable>
            }
            enabled={!isTabletScreen}
            extendedStyles={styles.xngTooltip}
            overlay={true}
            showArrow={false}
            drawer={true}
            title={
                <StyledWrapper {...styles.titleWrapper}>
                    <IconMemo width={21} height={25} color="text.main" src={HeaderCart} />
                    <Typography {...styles.title}>{translate("Cart")}</Typography>
                </StyledWrapper>
            }
            referenceId="mini-cart-reference"
        >
            <MiniCart closePopup={closePopup} isPopupShown={isMenuOpen} />
        </FloatingTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
